import { create } from 'zustand'
import type { MatchResultsResponse, PreviewResponse } from '@/types'

interface MatchingState {
  // Current match results
  currentMatch: MatchResultsResponse | null
  isLoadingResults: boolean
  resultsError: string | null

  // Preview data
  previewData: PreviewResponse['data'] | null
  isLoadingPreview: boolean
  previewError: string | null

  // Selected filters
  selectedMinScore: number
  selectedPage: number
  selectedLimit: number

  // Actions
  setCurrentMatch: (match: MatchResultsResponse | null) => void
  setLoadingResults: (loading: boolean) => void
  setResultsError: (error: string | null) => void

  setPreviewData: (preview: PreviewResponse['data'] | null) => void
  setLoadingPreview: (loading: boolean) => void
  setPreviewError: (error: string | null) => void

  setSelectedMinScore: (score: number) => void
  setSelectedPage: (page: number) => void
  setSelectedLimit: (limit: number) => void

  updateMatchResultFeedback: (resultId: string, feedback: 'RELEVANT' | 'NOT_RELEVANT' | 'SHORTLISTED', notes?: string) => void

  reset: () => void
}

const initialState = {
  currentMatch: null,
  isLoadingResults: false,
  resultsError: null,
  previewData: null,
  isLoadingPreview: false,
  previewError: null,
  selectedMinScore: 70,
  selectedPage: 1,
  selectedLimit: 20,
}

export const useMatchingStore = create<MatchingState>((set) => ({
  ...initialState,

  setCurrentMatch: (match) => set({ currentMatch: match }),
  setLoadingResults: (loading) => set({ isLoadingResults: loading }),
  setResultsError: (error) => set({ resultsError: error }),

  setPreviewData: (preview) => set({ previewData: preview }),
  setLoadingPreview: (loading) => set({ isLoadingPreview: loading }),
  setPreviewError: (error) => set({ previewError: error }),

  setSelectedMinScore: (score) => set({ selectedMinScore: score }),
  setSelectedPage: (page) => set({ selectedPage: page }),
  setSelectedLimit: (limit) => set({ selectedLimit: limit }),

  updateMatchResultFeedback: (resultId, feedback, notes) =>
    set((state) => {
      if (!state.currentMatch) return state

      return {
        currentMatch: {
          ...state.currentMatch,
          results: state.currentMatch.results.map((result) =>
            result.id === resultId
              ? { ...result, recruiterFeedback: feedback, feedbackNotes: notes }
              : result
          ),
        },
      }
    }),

  reset: () => set(initialState),
}))
